import { createRoute, redirect, useLoaderData, useRouterState } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { shareLinearConfigurationsConfirmationPageQueryOptions } from "~/app/api/pageData/shareLinearConfigurationsConfirmationPageQuery";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createShareLinearConfigurationConfirmationPage } from "~/features/configurator/Linear/components/ShareLinearConfigurationsConfirmationPage";

export type ShareLinearConfigurationsConfirmationRouteState = { numberOfConfigurations: number };

const SAVED_CONFIGS_PAGE = "/saved-configs";

export function requireShareLinearConfigurationsConfirmationState(
  state: unknown,
): ShareLinearConfigurationsConfirmationRouteState {
  const transientState = state as Partial<ShareLinearConfigurationsConfirmationRouteState> | undefined;
  if (typeof transientState?.numberOfConfigurations !== "number") {
    throw redirect({ href: SAVED_CONFIGS_PAGE });
  }
  return { numberOfConfigurations: transientState.numberOfConfigurations };
}

function ShareLinearConfigurationsConfirmationRoute() {
  const result = useLoaderData({
    from: "/sendLinearConfigurationsViaEmail/confirmation",
  }) as PageInitialData<void>;
  const shellProps = useStorefrontShellProps();
  const transientState = useRouterState({
    select: (s) => s.location.state,
  }) as ShareLinearConfigurationsConfirmationRouteState;

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createShareLinearConfigurationConfirmationPage({
        numberOfConfigurations: transientState.numberOfConfigurations,
      })}
    </StorefrontShellFrame>
  );
}

export async function loadShareLinearConfigurationsConfirmationPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(shareLinearConfigurationsConfirmationPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const shareLinearConfigurationsConfirmationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sendLinearConfigurationsViaEmail/confirmation",
  beforeLoad: ({ location }) => requireShareLinearConfigurationsConfirmationState(location.state),
  loader: async ({ context }) => loadShareLinearConfigurationsConfirmationPageData(context),
  component: ShareLinearConfigurationsConfirmationRoute,
});
